/**
 * The Create Template draft and every rule applied to it. Pure so the form, the summary panel and
 * the tests share one definition; the company service applies the same rules on create.
 */

export const WORKING_DAYS_PER_WEEK = 5;
export const DEFAULT_PHASE_DURATION_DAYS = 20;
export const NAME_MAX_LENGTH = 191;
// Enforced by the description's maxLength; the server refuses anything longer.
export const DESCRIPTION_MAX_LENGTH = 5000;
export const DELIVERABLE_NAME_MAX_LENGTH = 300;
export const DELIVERABLE_DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
export const START_WEEK_MAX = 520;
export const DURATION_DAYS_MAX = 2600;
export const HOURS_MAX = 10000;

export type Option = {id: string; label: string};

export type TimeEstimateDraft = {key: string; roleId: string | null; hours: number | null};

export type ExpenseEstimateDraft = {
	key: string;
	name: string;
	expenseCategoryId: string | null;
	revenue: number | null;
	cost: number | null;
};

export type PhaseDraft = {
	key: string;
	name: string;
	startWeek: number | null;
	durationDays: number | null;
	deliverableName: string;
	deliverableDescription: string;
	timeEstimates: TimeEstimateDraft[];
	expenseEstimates: ExpenseEstimateDraft[];
};

export type TemplateDraft = {
	name: string;
	description: string;
	includeDeliverables: boolean;
	phases: PhaseDraft[];
};

/** Field path → translation id, in on-screen order, so the first key is the field to scroll to. */
export type FieldErrors = Record<string, string>;

export type CreateBaselineTemplateRequest = {
	name: string;
	description: string | null;
	includeDeliverables: boolean;
	phases: {
		name: string;
		startWeek: number;
		durationDays: number;
		deliverableName: string | null;
		deliverableDescription: string | null;
		timeEstimates: {roleId: string; minutes: number}[];
		expenseEstimates: {name: string; expenseCategoryId: string; revenue: number; cost: number}[];
	}[];
};

let keySeed = 0;
const nextKey = (prefix: string): string => {
	keySeed += 1;
	return `${prefix}-${keySeed}`;
};

export const toNullableNumber = (value: number | null | undefined): number | null =>
	value === null || value === undefined || Number.isNaN(value) ? null : value;

export const blankTimeEstimate = (): TimeEstimateDraft => ({key: nextKey('time'), roleId: null, hours: null});

export const blankExpenseEstimate = (): ExpenseEstimateDraft => ({
	key: nextKey('expense'),
	name: '',
	expenseCategoryId: null,
	revenue: null,
	cost: null,
});

export const blankPhase = (startWeek: number): PhaseDraft => ({
	key: nextKey('phase'),
	name: '',
	startWeek,
	durationDays: DEFAULT_PHASE_DURATION_DAYS,
	deliverableName: '',
	deliverableDescription: '',
	timeEstimates: [blankTimeEstimate()],
	expenseEstimates: [],
});

export const blankTemplate = (): TemplateDraft => ({
	name: '',
	description: '',
	includeDeliverables: false,
	phases: [blankPhase(1)],
});

const isBlank = (value: string): boolean => value.trim() === '';

const isWholeInRange = (value: number | null, max: number): value is number =>
	value !== null && Number.isInteger(value) && value >= 1 && value <= max;

export const phaseEndDay = (phase: PhaseDraft): number =>
	isWholeInRange(phase.startWeek, START_WEEK_MAX) && isWholeInRange(phase.durationDays, DURATION_DAYS_MAX)
		? (phase.startWeek - 1) * WORKING_DAYS_PER_WEEK + phase.durationDays
		: 0;

export const templateDurationDays = (draft: TemplateDraft): number => Math.max(0, ...draft.phases.map(phaseEndDay));

export const weeksForDays = (days: number): number => Math.ceil(days / WORKING_DAYS_PER_WEEK);

export const nextPhaseStartWeek = (draft: TemplateDraft): number =>
	Math.ceil((templateDurationDays(draft) + 1) / WORKING_DAYS_PER_WEEK);

export const addPhase = (draft: TemplateDraft): TemplateDraft => ({
	...draft,
	phases: [...draft.phases, blankPhase(nextPhaseStartWeek(draft))],
});

export const removePhase = (draft: TemplateDraft, phaseKey: string): TemplateDraft =>
	draft.phases.length <= 1 ? draft : {...draft, phases: draft.phases.filter(phase => phase.key !== phaseKey)};

export const updatePhase = (draft: TemplateDraft, phaseKey: string, patch: Partial<PhaseDraft>): TemplateDraft => ({
	...draft,
	phases: draft.phases.map(phase => (phase.key === phaseKey ? {...phase, ...patch} : phase)),
});

const isFiniteNumber = (value: number | null): value is number => value !== null && Number.isFinite(value);

/** A missing, non-finite or zero amount counts as not entered. */
const isEmptyAmount = (value: number | null): boolean => !isFiniteNumber(value) || value === 0;

const MINUTES_PER_HOUR = 60;

/** Whole minutes as saved for a row; a missing or non-finite value saves nothing. */
const savedMinutes = (hours: number | null): number => (isFiniteNumber(hours) ? Math.round(hours * MINUTES_PER_HOUR) : 0);

export const isUntouchedTimeEstimate = (row: TimeEstimateDraft): boolean => row.roleId === null && isEmptyAmount(row.hours);

export const isUntouchedExpenseEstimate = (row: ExpenseEstimateDraft): boolean =>
	isBlank(row.name) && row.expenseCategoryId === null && isEmptyAmount(row.revenue) && isEmptyAmount(row.cost);

const phaseMinutes = (phase: PhaseDraft): number => phase.timeEstimates.reduce((sum, row) => sum + savedMinutes(row.hours), 0);

/** Hours as they will be saved: each row rounds to whole minutes first, then the minutes are summed. */
export const phaseHours = (phase: PhaseDraft): number => phaseMinutes(phase) / MINUTES_PER_HOUR;

export const totalHours = (draft: TemplateDraft): number =>
	draft.phases.reduce((sum, phase) => sum + phaseMinutes(phase), 0) / MINUTES_PER_HOUR;

export const deliverableCount = (draft: TemplateDraft): number | null =>
	draft.includeDeliverables ? draft.phases.filter(phase => !isBlank(phase.deliverableName)).length : null;

export const timeEstimateCount = (phase: PhaseDraft): number =>
	phase.timeEstimates.filter(row => !isUntouchedTimeEstimate(row)).length;

export const expenseEstimateCount = (phase: PhaseDraft): number =>
	phase.expenseEstimates.filter(row => !isUntouchedExpenseEstimate(row)).length;

export const expenseLineCount = (draft: TemplateDraft): number =>
	draft.phases.reduce((sum, phase) => sum + expenseEstimateCount(phase), 0);

/** Keys of the rows in a phase patch that are untouched again, so the form can drop their errors. */
export const untouchedRowKeys = (patch: Partial<PhaseDraft>): string[] => [
	...(patch.timeEstimates ?? []).filter(isUntouchedTimeEstimate).map(row => row.key),
	...(patch.expenseEstimates ?? []).filter(isUntouchedExpenseEstimate).map(row => row.key),
];

// Keys are React bookkeeping, not content, so they are left out of the comparison. Deliverable
// fields only count while deliverables are on: with them off nothing typed there is sent.
const comparable = (draft: TemplateDraft) => ({
	name: draft.name,
	description: draft.description,
	includeDeliverables: draft.includeDeliverables,
	phases: draft.phases.map(phase => ({
		name: phase.name,
		startWeek: phase.startWeek,
		durationDays: phase.durationDays,
		deliverableName: draft.includeDeliverables ? phase.deliverableName : '',
		deliverableDescription: draft.includeDeliverables ? phase.deliverableDescription : '',
		timeEstimates: phase.timeEstimates.map(row => ({roleId: row.roleId, hours: row.hours})),
		expenseEstimates: phase.expenseEstimates.map(row => ({
			name: row.name,
			expenseCategoryId: row.expenseCategoryId,
			revenue: row.revenue,
			cost: row.cost,
		})),
	})),
});

let blankSnapshot: string | null = null;

/** Compares against a blank draft field by field, so an edit undone by hand is not dirty. */
export const isDirty = (draft: TemplateDraft): boolean => {
	// Built once on first use: blankTemplate() hands out row keys, and a check must not use any up.
	blankSnapshot ??= JSON.stringify(comparable(blankTemplate()));
	return JSON.stringify(comparable(draft)) !== blankSnapshot;
};

export const isNameTaken = (name: string, existingNames: readonly string[]): boolean => {
	const wanted = name.trim().toLowerCase();
	return wanted !== '' && existingNames.some(existing => existing.trim().toLowerCase() === wanted);
};

const validateTimeEstimate = (row: TimeEstimateDraft, errors: FieldErrors): void => {
	if (isUntouchedTimeEstimate(row)) {
		return;
	}
	if (row.roleId === null) {
		errors[`${row.key}.roleId`] = 'deliverable.select_role';
	}
	if (!isFiniteNumber(row.hours) || Math.round(row.hours * MINUTES_PER_HOUR) < 1) {
		if (row.roleId !== null) {
			errors[`${row.key}.hours`] = 'baseline_templates.errors.enter_hours';
		}
	} else if (row.hours > HOURS_MAX) {
		errors[`${row.key}.hours`] = 'baseline_templates.errors.hours_too_high';
	}
};

const validateExpenseEstimate = (row: ExpenseEstimateDraft, errors: FieldErrors): void => {
	if (isUntouchedExpenseEstimate(row)) {
		return;
	}
	if (isBlank(row.name)) {
		errors[`${row.key}.name`] = 'baseline_templates.errors.expense_name_required';
	} else if (row.name.trim().length > NAME_MAX_LENGTH) {
		errors[`${row.key}.name`] = 'baseline_templates.errors.name_too_long';
	}
	if (row.expenseCategoryId === null) {
		errors[`${row.key}.expenseCategoryId`] = 'deliverable.select_expense_category';
	}
};

const validatePhase = (phase: PhaseDraft, includeDeliverables: boolean, errors: FieldErrors): void => {
	if (isBlank(phase.name)) {
		errors[`${phase.key}.name`] = 'baseline_templates.errors.phase_name_required';
	} else if (phase.name.trim().length > NAME_MAX_LENGTH) {
		errors[`${phase.key}.name`] = 'baseline_templates.errors.name_too_long';
	}
	if (!isWholeInRange(phase.startWeek, START_WEEK_MAX)) {
		errors[`${phase.key}.startWeek`] = 'baseline_templates.errors.start_week_invalid';
	}
	if (!isWholeInRange(phase.durationDays, DURATION_DAYS_MAX)) {
		errors[`${phase.key}.durationDays`] = 'baseline_templates.errors.duration_invalid';
	}
	if (includeDeliverables && phase.deliverableName.trim().length > DELIVERABLE_NAME_MAX_LENGTH) {
		errors[`${phase.key}.deliverableName`] = 'baseline_templates.errors.deliverable_name_too_long';
	}
	phase.timeEstimates.forEach(row => validateTimeEstimate(row, errors));
	phase.expenseEstimates.forEach(row => validateExpenseEstimate(row, errors));
};

export const validate = (draft: TemplateDraft, existingNames: readonly string[]): FieldErrors => {
	const errors: FieldErrors = {};
	const name = draft.name.trim();
	if (name === '') {
		errors.name = 'baseline_templates.errors.name_required';
	} else if (name.length > NAME_MAX_LENGTH) {
		errors.name = 'baseline_templates.errors.name_too_long';
	} else if (isNameTaken(name, existingNames)) {
		errors.name = 'baseline_templates.errors.name_taken';
	}
	draft.phases.forEach(phase => validatePhase(phase, draft.includeDeliverables, errors));
	return errors;
};

/** Only call on a draft that `validate` accepted. */
export const normalise = (draft: TemplateDraft): CreateBaselineTemplateRequest => ({
	name: draft.name.trim(),
	description: isBlank(draft.description) ? null : draft.description.trim(),
	includeDeliverables: draft.includeDeliverables,
	phases: draft.phases.map(phase => {
		const deliverableName = draft.includeDeliverables ? phase.deliverableName.trim() : '';
		return {
			name: phase.name.trim(),
			startWeek: phase.startWeek as number,
			durationDays: phase.durationDays as number,
			deliverableName: deliverableName || null,
			deliverableDescription:
				deliverableName && !isBlank(phase.deliverableDescription) ? phase.deliverableDescription.trim() : null,
			timeEstimates: phase.timeEstimates
				.filter(row => !isUntouchedTimeEstimate(row))
				.map(row => ({roleId: row.roleId as string, minutes: savedMinutes(row.hours)})),
			expenseEstimates: phase.expenseEstimates
				.filter(row => !isUntouchedExpenseEstimate(row))
				.map(row => ({
					name: row.name.trim(),
					expenseCategoryId: row.expenseCategoryId as string,
					revenue: isFiniteNumber(row.revenue) ? row.revenue : 0,
					cost: isFiniteNumber(row.cost) ? row.cost : 0,
				})),
		};
	}),
});
